/**
 * SkillDelivery Platform
 * S-Curve Chart Component - SVG-based EVM visualization
 *
 * Conference Sprint S2c: Project progress visualization
 * Shows PV (Planned Value), EV (Earned Value), AC (Actual Cost) over time
 */

import React, { useState } from 'react';
import './SCurveChart.css';

const SCurveChart = ({ data, currentMonth }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Chart dimensions
  const width = 800;
  const height = 350;
  const padding = { top: 40, right: 40, bottom: 60, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate scales
  const maxMonth = Math.max(...data.map(d => d.month));
  const maxValue = Math.max(...data.map(d => Math.max(d.pv || 0, d.ev || 0, d.ac || 0))) * 1.1;

  const xScale = (month) => padding.left + (month / maxMonth) * chartWidth;
  const yScale = (value) => padding.top + chartHeight - (value / maxValue) * chartHeight;

  // Generate path for each line
  const generatePath = (dataKey, filterNulls = false) => {
    const points = data
      .filter(d => !filterNulls || d[dataKey] !== null)
      .map(d => `${xScale(d.month)},${yScale(d[dataKey] || 0)}`);
    return `M ${points.join(' L ')}`;
  };

  // Generate Y-axis ticks
  const yTicks = [];
  const tickCount = 5;
  for (let i = 0; i <= tickCount; i++) {
    const value = (maxValue / tickCount) * i;
    yTicks.push({ value, y: yScale(value) });
  }

  // Generate X-axis ticks
  const xTicks = data.map(d => ({
    month: d.month,
    x: xScale(d.month)
  }));

  // Get data points for dots
  const pvPoints = data.map(d => ({ month: d.month, value: d.pv, x: xScale(d.month), y: yScale(d.pv) }));
  const evPoints = data.filter(d => d.ev !== null).map(d => ({ month: d.month, value: d.ev, x: xScale(d.month), y: yScale(d.ev) }));
  const acPoints = data.filter(d => d.ac !== null).map(d => ({ month: d.month, value: d.ac, x: xScale(d.month), y: yScale(d.ac) }));

  return (
    <div className="scurve-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="scurve-svg">
        {/* Grid lines */}
        <g className="grid-lines">
          {yTicks.map((tick, i) => (
            <line
              key={`y-grid-${i}`}
              x1={padding.left}
              x2={width - padding.right}
              y1={tick.y}
              y2={tick.y}
              stroke="#e5e7eb"
              strokeDasharray="4 4"
            />
          ))}
          {xTicks.map((tick, i) => (
            <line
              key={`x-grid-${i}`}
              x1={tick.x}
              x2={tick.x}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#e5e7eb"
              strokeDasharray="4 4"
            />
          ))}
        </g>

        {/* Current month reference line */}
        <line
          x1={xScale(currentMonth)}
          x2={xScale(currentMonth)}
          y1={padding.top}
          y2={height - padding.bottom}
          stroke="#6366f1"
          strokeWidth="2"
          strokeDasharray="6 4"
        />
        <text
          x={xScale(currentMonth)}
          y={padding.top - 10}
          textAnchor="middle"
          fill="#6366f1"
          fontSize="12"
          fontWeight="600"
        >
          Today
        </text>

        {/* Axes */}
        <line
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={height - padding.bottom}
          stroke="#9ca3af"
          strokeWidth="1"
        />
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={height - padding.bottom}
          y2={height - padding.bottom}
          stroke="#9ca3af"
          strokeWidth="1"
        />

        {/* Y-axis labels */}
        {yTicks.map((tick, i) => (
          <text
            key={`y-label-${i}`}
            x={padding.left - 10}
            y={tick.y + 4}
            textAnchor="end"
            fill="#6b7280"
            fontSize="11"
          >
            ₹{tick.value.toFixed(0)}L
          </text>
        ))}

        {/* X-axis labels */}
        {xTicks.map((tick, i) => (
          <text
            key={`x-label-${i}`}
            x={tick.x}
            y={height - padding.bottom + 20}
            textAnchor="middle"
            fill="#6b7280"
            fontSize="11"
          >
            M{tick.month}
          </text>
        ))}

        {/* Axis titles */}
        <text
          x={width / 2}
          y={height - 10}
          textAnchor="middle"
          fill="#374151"
          fontSize="12"
          fontWeight="500"
        >
          Month
        </text>
        <text
          x={15}
          y={height / 2}
          textAnchor="middle"
          fill="#374151"
          fontSize="12"
          fontWeight="500"
          transform={`rotate(-90, 15, ${height / 2})`}
        >
          Value (₹ Lakhs)
        </text>

        {/* PV Line (dashed - planned) */}
        <path
          d={generatePath('pv')}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeDasharray="8 4"
          className="line-pv"
        />

        {/* EV Line (solid - earned) */}
        <path
          d={generatePath('ev', true)}
          fill="none"
          stroke="#22c55e"
          strokeWidth="3"
          className="line-ev"
        />

        {/* AC Line (solid - actual cost) */}
        <path
          d={generatePath('ac', true)}
          fill="none"
          stroke="#ef4444"
          strokeWidth="3"
          className="line-ac"
        />

        {/* PV Points */}
        {pvPoints.map((point, i) => (
          <circle
            key={`pv-${i}`}
            cx={point.x}
            cy={point.y}
            r={hoveredPoint?.month === point.month ? 8 : 5}
            fill="#3b82f6"
            stroke="white"
            strokeWidth="2"
            className="data-point"
            onMouseEnter={() => setHoveredPoint({ ...point, type: 'pv' })}
            onMouseLeave={() => setHoveredPoint(null)}
          />
        ))}

        {/* EV Points */}
        {evPoints.map((point, i) => (
          <circle
            key={`ev-${i}`}
            cx={point.x}
            cy={point.y}
            r={hoveredPoint?.month === point.month ? 8 : 6}
            fill="#22c55e"
            stroke="white"
            strokeWidth="2"
            className="data-point"
            onMouseEnter={() => setHoveredPoint({ ...point, type: 'ev' })}
            onMouseLeave={() => setHoveredPoint(null)}
          />
        ))}

        {/* AC Points */}
        {acPoints.map((point, i) => (
          <circle
            key={`ac-${i}`}
            cx={point.x}
            cy={point.y}
            r={hoveredPoint?.month === point.month ? 8 : 6}
            fill="#ef4444"
            stroke="white"
            strokeWidth="2"
            className="data-point"
            onMouseEnter={() => setHoveredPoint({ ...point, type: 'ac' })}
            onMouseLeave={() => setHoveredPoint(null)}
          />
        ))}

        {/* Tooltip */}
        {hoveredPoint && (
          <g className="tooltip-group">
            <rect
              x={hoveredPoint.x - 50}
              y={hoveredPoint.y - 45}
              width="100"
              height="35"
              fill="white"
              stroke="#e5e7eb"
              rx="6"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
            />
            <text
              x={hoveredPoint.x}
              y={hoveredPoint.y - 30}
              textAnchor="middle"
              fill="#374151"
              fontSize="11"
              fontWeight="600"
            >
              Month {hoveredPoint.month}
            </text>
            <text
              x={hoveredPoint.x}
              y={hoveredPoint.y - 16}
              textAnchor="middle"
              fill={hoveredPoint.type === 'pv' ? '#3b82f6' : hoveredPoint.type === 'ev' ? '#22c55e' : '#ef4444'}
              fontSize="12"
              fontWeight="600"
            >
              ₹{hoveredPoint.value?.toFixed(2)}L
            </text>
          </g>
        )}
      </svg>

      {/* Legend */}
      <div className="scurve-legend">
        <div className="legend-item">
          <span className="legend-line pv"></span>
          <span className="legend-label">Planned Value (PV)</span>
        </div>
        <div className="legend-item">
          <span className="legend-line ev"></span>
          <span className="legend-label">Earned Value (EV)</span>
        </div>
        <div className="legend-item">
          <span className="legend-line ac"></span>
          <span className="legend-label">Actual Cost (AC)</span>
        </div>
      </div>

      <div className="scurve-legend-notes">
        <div className="legend-note">
          <span className="dot blue"></span>
          <span>PV above EV = Behind Schedule</span>
        </div>
        <div className="legend-note">
          <span className="dot red"></span>
          <span>AC above EV = Over Budget</span>
        </div>
        <div className="legend-note">
          <span className="dot green"></span>
          <span>EV = Value of work actually completed</span>
        </div>
      </div>
    </div>
  );
};

export default SCurveChart;
